import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAbbreviationBadge,
  MiButton,
  MiCard,
  MiDatePicker,
  MiDetailFields,
  MiList,
  MiLoader,
  MiModal,
  MiStackedTypography,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
  INPUT_TYPES,
  ORDER_STATUS_COLORS,
  ORDER_TYPES_OPTIONS,
  PERMISSIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import {
  addSpaces,
  addToDate,
  alphaColor,
  getContrastColor,
  getDate,
  isEmpty,
  toFormattedDate,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { usePermissions } from "../../hooks";
import { getHomePhase } from "../../utils/getHomePhase/getHomePhase";
import { HomeInformation } from "./HomeInformation/HomeInformation";
import { OrderInfoPopper } from "./OrderInfoPopper/OrderInfoPopper";
import { WorkOrderCount } from "./WorkOrderCount/WorkOrderCount";
import { useOrderTemplateSelectionViewModel } from "./useOrderTemplateSelectionViewModel";

const PLACEHOLDER_TEXT = "--/--/--";
const DATE_FORMAT = "MM/DD/YY";

const MAIN_TAB_NAMES = {
  ORDER_CREATION: "Order Creation",
  ORDER_HISTORY: "Order History",
};

const MAIN_TABS = Object.keys(MAIN_TAB_NAMES).map((key) => ({
  value: MAIN_TAB_NAMES[key],
  label: MAIN_TAB_NAMES[key],
}));

const isOrderTemplateDisabled = (orderTemplate, takeOverOrderTemplate) => {
  return (
    orderTemplate.fulfillmentSequence &&
    takeOverOrderTemplate &&
    orderTemplate.fulfillmentSequence <
      takeOverOrderTemplate.fulfillmentSequence
  );
};

const getColor = (status) => ORDER_STATUS_COLORS[status];

function Section({
  name,
  orderTemplates: allOrderTemplates,
  selectedOrderTemplate,
  takeOverOrderTemplate,
  handleOrderTemplateSelection,
}) {
  const { classes, theme } = useStyles();

  const orderTemplates = allOrderTemplates.filter(({ homePhases }) =>
    homePhases.includes(name)
  );

  const getBackgroundColor = (item) => {
    if (item.id === selectedOrderTemplate.id) {
      return theme.palette.yellow.lightAccent;
    }

    return theme.palette.background.default;
  };

  const data = orderTemplates
    .sort((a, b) => {
      if (!a.fulfillmentSequence && !b.fulfillmentSequence) {
        return 0;
      }
      if (!a.fulfillmentSequence) {
        return 1;
      }
      if (!b.fulfillmentSequence) {
        return -1;
      }

      return a.fulfillmentSequence - b.fulfillmentSequence;
    })
    .map((item) => ({
      ...item,
      containerStyles: {
        backgroundColor: getBackgroundColor(item),
      },
    }));

  if (isEmpty(data)) {
    return (
      <Box className={classes.placeholderContainer}>
        <Typography className={classes.placeholderText}>
          No Available Orders
        </Typography>
        <Typography className={classes.placeholderSecondaryText}>
          Get started by creating a new order for this home
        </Typography>
      </Box>
    );
  }

  return (
    <MiList
      viewType="card"
      hideListPagination
      data={data}
      cardProps={{
        leftCardProps: {
          leftWidth: "40%",
        },
        renderTitle: ({
          name,
          abbreviation,
          tradeName,
          color,
          ...orderTemplate
        }) => (
          <Box
            className={classes.titleContainer}
            sx={
              isOrderTemplateDisabled(orderTemplate, takeOverOrderTemplate)
                ? { opacity: 0.5 }
                : {}
            }
          >
            <MiAbbreviationBadge
              abbreviation={abbreviation}
              backgroundColor={color}
              disabled={isOrderTemplateDisabled(
                orderTemplate,
                takeOverOrderTemplate
              )}
            />

            <MiStackedTypography
              title={name}
              description={tradeName}
              titleFontSize={theme.fontSize.medium}
              descriptionFontSize={theme.fontSize.small}
              titleProps={{
                fontWeight: theme.fontWeight.bold,
                color: theme.palette.cyan.dark,
              }}
              descriptionProps={{
                color: theme.palette.grey.medium,
              }}
            />
          </Box>
        ),
        renderItemActions: (orderTemplate) => {
          return (
            <MiButton
              title="Schedule"
              startIcon={{
                path: mdi[ICONS_WEB.PLUS],
                iconSize: 0.7,
              }}
              variant={BUTTON_VARIANTS.CONTAINED}
              className={classes.scheduleBtn}
              size="small"
              disabled={
                !isEmpty(selectedOrderTemplate) ||
                isOrderTemplateDisabled(orderTemplate, takeOverOrderTemplate)
              }
              onClick={() => {
                handleOrderTemplateSelection(orderTemplate);
              }}
            />
          );
        },
      }}
    />
  );
}

function OrderHistory({ orders }) {
  const { classes, theme } = useStyles();

  const sections = [
    {
      fieldsetStyle: {
        justifyContent: "flex-end",
        alignItems: "center",
      },
      fieldset: [
        {
          renderCell: (item) => {
            return (
              <Box className={classes.historyInfoContainer}>
                <MiStackedTypography
                  title="Work Orders"
                  descriptionProps={{
                    children: <WorkOrderCount order={item} />,
                  }}
                />
                {item.approvedStartDate ? (
                  <MiStackedTypography
                    title="Scheduled"
                    description={
                      item.approvedStartDate
                        ? toFormattedDate(item.approvedStartDate, DATE_FORMAT)
                        : PLACEHOLDER_TEXT
                    }
                  />
                ) : (
                  <MiStackedTypography
                    title="Requested"
                    description={
                      item.requestedStartDate
                        ? toFormattedDate(item.requestedStartDate, DATE_FORMAT)
                        : PLACEHOLDER_TEXT
                    }
                  />
                )}
                <MiStackedTypography
                  title="Completed"
                  description={
                    item.completeDate
                      ? toFormattedDate(item.completeDate, DATE_FORMAT)
                      : PLACEHOLDER_TEXT
                  }
                />
              </Box>
            );
          },
        },
      ],
    },
  ];

  if (isEmpty(orders)) {
    return (
      <Box className={classes.placeholderContainer}>
        <Typography className={classes.placeholderText}>
          No Orders Found
        </Typography>
        <Typography className={classes.placeholderSecondaryText}>
          Get started by creating a new order for this home
        </Typography>
      </Box>
    );
  }

  const styledData = orders.map((order) => ({
    ...order,
    containerStyles: {
      backgroundColor: alphaColor(getColor(order.orderStatus), 0.1),
    },
  }));

  return (
    <MiList
      viewType="card"
      hideListPagination
      data={styledData}
      cardProps={{
        sections,
        leftCardProps: {
          leftWidth: "40%",
        },
        renderTitle: (order) => {
          const {
            orderStatus,
            orderTemplateAbbreviation,
            orderTemplateColor,
            orderTemplateName,
            orderTemplateTradeName,
          } = order;

          return (
            <Box className={classes.historyTitleContainer}>
              <MiStackedTypography
                className={classes.historyInfoTitle}
                title="Status"
                description={addSpaces(orderStatus)}
                descriptionProps={{
                  color: getColor(orderStatus),
                  fontWeight: theme.fontWeight.bold,
                }}
              />
              <Box
                className={classes.abbreviationContainer}
                backgroundColor={orderTemplateColor}
              >
                <Typography
                  fontSize={theme.fontSize.xsmall}
                  color={getContrastColor(orderTemplateColor)}
                >
                  {orderTemplateAbbreviation}
                </Typography>
              </Box>
              <MiStackedTypography
                description={orderTemplateTradeName}
                descriptionFontSize={theme.fontSize.small}
                titleProps={{
                  children: (
                    <Box className={classes.textContainter}>
                      <Typography
                        fontSize={theme.fontSize.medium}
                        fontWeight={theme.fontWeight.semiBold}
                        color={theme.palette.cyan.dark}
                      >
                        {orderTemplateName}
                      </Typography>
                      <OrderInfoPopper order={order} />
                    </Box>
                  ),
                }}
                descriptionProps={{
                  color: theme.palette.grey.medium,
                }}
              />
            </Box>
          );
        },
      }}
    />
  );
}

function CloseHomeButton({ home, homeBuilderDetails = null, updateHome }) {
  const INITIAL_VALUES = useMemo(
    () => ({
      closingDate: home.closingDate || null,
      warrantyEndDate: home.warrantyEndDate || null,
    }),
    [home.closingDate, home.warrantyEndDate]
  );
  const [openCloseHomeModal, setOpenCloseHomeModal] = useState(false);
  const edit = useEdit(INITIAL_VALUES);
  if (home.closingDate) {
    return null;
  }
  const onClosingDateChange = (date) => {
    const warrantyPeriodInMonths = homeBuilderDetails?.warrantyPeriodInMonths;
    edit.update({
      closingDate: date
        ? getDate(date).format(DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT)
        : null,
      warrantyEndDate:
        date && warrantyPeriodInMonths
          ? addToDate(date, warrantyPeriodInMonths, "months").format(
              DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
            )
          : null,
    });
  };

  return (
    <Box flex={1} display="flex" justifyContent="flex-end">
      <MiModal
        open={openCloseHomeModal}
        closeModal={() => {
          setOpenCloseHomeModal(false);
          edit.reset(INITIAL_VALUES);
        }}
        titleIcon={mdi.mdiHomeRoof}
        title="Close Home"
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.OUTLINED,
            onClick: () => {
              setOpenCloseHomeModal(false);
              edit.reset(INITIAL_VALUES);
            },
            color: BUTTON_COLORS.ERROR,
          },
          {
            name: "Save",
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => {
              updateHome(edit.getValue());
              setOpenCloseHomeModal(false);
            },
            disabled: !edit.isDirty(),
            color: BUTTON_COLORS.PRIMARY,
          },
        ]}
      >
        <MiDetailFields
          edit={edit}
          detailFields={[
            {
              label: "Closing Date",
              name: "closingDate",
              fieldType: INPUT_TYPES.CUSTOM,
              customComponent: (
                <MiDatePicker
                  label="Closing Date"
                  isFullWidth
                  onChange={onClosingDateChange}
                  isShrink
                  variant={DATEPICKER_VARIANTS.DESKTOP}
                  actions={[
                    DATEPICKER_ACTIONS.TODAY,
                    DATEPICKER_ACTIONS.CLEAR,
                    DATEPICKER_ACTIONS.CANCEL,
                  ]}
                />
              ),
              width: "100%",
            },
            {
              label: "Warranty End Date",
              name: "warrantyEndDate",
              fieldType: INPUT_TYPES.CUSTOM,
              customComponent: (
                <MiDatePicker
                  label="Warranty End Date"
                  disabled
                  isFullWidth
                  onChange={(date) => {
                    edit.update({
                      warrantyEndDate: date
                        ? getDate(date).format(
                            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
                          )
                        : null,
                    });
                  }}
                  isShrink
                  variant={DATEPICKER_VARIANTS.DESKTOP}
                  actions={[
                    DATEPICKER_ACTIONS.TODAY,
                    DATEPICKER_ACTIONS.CLEAR,
                    DATEPICKER_ACTIONS.CANCEL,
                  ]}
                />
              ),
              width: "100%",
            },
          ]}
        />
      </MiModal>
      <MiButton
        variant={BUTTON_VARIANTS.OUTLINED}
        size={BUTTON_SIZES.SMALL}
        color="primary"
        title="Close Home"
        onClick={() => setOpenCloseHomeModal(true)}
      />
    </Box>
  );
}

export function OrderTemplateSelection({
  home = {},
  goBack = () => {},
  selectedOrderTemplate = {},
  handleOrderTemplateSelection = () => null,
  defaultActiveTab,
}) {
  const {
    HOME_PHASE_MAP,
    isLoading,
    orderTemplates,
    homeOrders,
    homeDetails = home,
    activeOrderCreationTab,
    activeMainTab,
    homeBuilderDetails,
    updateHome,
    setActiveMainTab,
    setActiveOrderCreationTab,
    getHomeDetails,
    loadAllOrderTemplates,
    getHomeOrders,
  } = useOrderTemplateSelectionViewModel({ homeId: home.id, defaultActiveTab });

  const { hasPermission } = usePermissions();
  const ableToCloseHome = hasPermission(PERMISSIONS.HOME.CLOSE);

  const homePhase = getHomePhase(homeDetails);

  const orderCreationTabs = ORDER_TYPES_OPTIONS.map((tab) => ({
    ...tab,
    disabled: !HOME_PHASE_MAP[homePhase].enabledTabs.includes(tab.value),
  }));

  const takeOverOrderTemplate = useMemo(() => {
    if (!home.takeoverStageId) {
      return null;
    }

    return orderTemplates.find(
      (template) => template.id === home.takeoverStageId
    );
  }, [orderTemplates, home.takeoverStageId]);

  useEffect(() => {
    if (home?.id) {
      getHomeDetails();
      loadAllOrderTemplates();
      getHomeOrders();
    }
  }, [home]);

  const { classes, theme } = useStyles();

  return (
    <Box className={classes.root}>
      <HomeInformation
        home={homeDetails}
        onRightIconClick={goBack}
        rightComponent={
          ableToCloseHome ? (
            <CloseHomeButton
              home={homeDetails}
              homeBuilderDetails={homeBuilderDetails}
              updateHome={updateHome}
            />
          ) : null
        }
      />
      <MiTab
        tabs={MAIN_TABS}
        currentTab={activeMainTab}
        scrollButtons="none"
        onTabChange={setActiveMainTab}
        chipStyle={{
          height: 18,
          minWidth: 18,
          width: "fit-content",
          borderRadius: "100%",
          "& .MuiChip-label": {
            ...theme.typography.body2,
            fontSize: theme.fontSize.xsmall,
            padding: theme.spacing(0, 1),
          },
        }}
        backgroundColor="inherit"
        borderBottom
      />
      {activeMainTab === MAIN_TAB_NAMES.ORDER_CREATION && (
        <Box className={classes.container}>
          <MiCard
            title="Orders"
            leftIcon={mdi[ICONS_WEB.BULLET_LIST]}
            containerStyles={{ padding: theme.spacing(2) }}
          >
            <MiTab
              tabs={orderCreationTabs}
              currentTab={activeOrderCreationTab}
              onTabChange={setActiveOrderCreationTab}
              chipStyle={{
                height: 18,
                minWidth: 18,
                width: "fit-content",
                borderRadius: "100%",
                "& .MuiChip-label": {
                  ...theme.typography.body2,
                  fontSize: theme.fontSize.xsmall,
                  padding: theme.spacing(0, 1),
                },
              }}
              backgroundColor="inherit"
              borderBottom
            />
            <MiLoader isLoading={isLoading} type="linear" />
            <Section
              name={activeOrderCreationTab}
              homeOrders={homeOrders}
              orderTemplates={orderTemplates}
              selectedOrderTemplate={selectedOrderTemplate}
              takeOverOrderTemplate={takeOverOrderTemplate}
              handleOrderTemplateSelection={handleOrderTemplateSelection}
            />
          </MiCard>
        </Box>
      )}
      {activeMainTab === MAIN_TAB_NAMES.ORDER_HISTORY && (
        <OrderHistory orders={homeOrders} />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    flexGrow: 1,
    height: "100%",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: 0,
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  historyTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    marginLeft: theme.spacing(1),
  },
  historyInfoContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    gap: theme.spacing(4),
    paddingRight: theme.spacing(1),
  },
  historyInfoTitle: {
    width: 80,
  },
  abbreviationContainer: {
    borderRadius: theme.spacing(0.5),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 30,
    height: 20,
  },
  scheduleBtn: {
    padding: theme.spacing(0.5, 4),
    borderRadius: theme.spacing(1),
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    height: 150,
  },
  placeholderText: {
    fontWeight: theme.fontWeight.medium,
    fontSize: theme.fontSize.xlarge,
  },
  placeholderSecondaryText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.grey.medium,
  },
  textContainter: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
}));
